/**
 * Display helpers for the Equations page — pure, so they can be read (and tested) without React.
 */
import type { EquationDefinition, EquationGroup, PolicyError } from 'used-vehicle-contracts';
import { CUSTOM_ID_PATTERN, type EquationDef, type UnitName } from 'used-vehicle-engine';

/** The order plan §7 draws the sections in. */
export const EQUATION_GROUP_ORDER: readonly EquationGroup[] = ['AGE_VALUE', 'INSPECTION', 'COSTS', 'ACQUISITION', 'TRADE_IN'];

export const UNIT_OPTIONS: readonly UnitName[] = ['JOD', 'fraction', 'km', 'L', 'days', 'years', 'count', 'flag', 'JOD/day', 'JOD/L', 'km/year'];

/** Dimensionless results carry more decimals than money, so a rate does not round away to nothing. */
const decimalsFor = (unit: UnitName): number => (unit === 'fraction' || unit === 'count' || unit === 'flag' ? 6 : 3);

/**
 * `= 15,820.500 JOD`. Latin digits and LTR even in Arabic — a price is read as a number, not as prose
 * (plan §2.7).
 *
 * JOD keeps all three decimals, because the third one is the unit the platform records money in. Every
 * other unit drops its trailing zeros: an age is "2 years", not "2.000 years", and a rate is "0.005"
 * rather than "0.005000".
 */
export const formatEquationValue = (value: number, unit: UnitName, unitLabel: string): string => {
  const fixed = value.toFixed(decimalsFor(unit));
  const trimmed = unit === 'JOD' ? fixed : String(Number(fixed));
  const [whole, fraction] = trimmed.split('.');
  const grouped = new Intl.NumberFormat('en-US').format(Number(whole));

  return `${fraction === undefined ? grouped : `${grouped}.${fraction}`} ${unitLabel}`;
};

export const formatEquationResult = (value: number, unit: UnitName, unitLabel: string): string => `= ${formatEquationValue(value, unit, unitLabel)}`;

/** Groups the equations for rendering while preserving the order they are stored in. */
export const groupEquations = (equations: readonly EquationDefinition[]): Map<EquationGroup, EquationDefinition[]> => {
  const grouped = new Map<EquationGroup, EquationDefinition[]>(EQUATION_GROUP_ORDER.map((group) => [group, []]));

  for (const equation of equations) {
    const bucket = grouped.get(equation.group);

    if (bucket) {
      bucket.push(equation);
    } else {
      grouped.set(equation.group, [equation]);
    }
  }

  return grouped;
};

/** Every equation that mentions `id` in its expression — a custom equation is only removable when none do. */
export const referencesTo = (equations: readonly EquationDefinition[], id: string): string[] => {
  const pattern = new RegExp(`\\b${id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);

  return equations.filter((equation) => equation.id !== id && pattern.test(equation.expression)).map((equation) => equation.id);
};

/** `x_a`, `x_b`, … — the first form that is not taken. */
export const nextCustomId = (equations: readonly EquationDefinition[]): string => {
  const taken = new Set(equations.map((equation) => equation.id));

  for (let index = 1; index < 400; index += 1) {
    const candidate = `x_value${index}`;

    if (!taken.has(candidate)) {
      return candidate;
    }
  }

  return `x_value${Date.now()}`;
};

export const isValidCustomId = (id: string): boolean => CUSTOM_ID_PATTERN.test(id);

/** The exact text the server validated, so Publish can insist the draft has not moved since. */
export const equationsSignature = (equations: readonly EquationDefinition[]): string =>
  JSON.stringify(
    equations.map((equation) => [
      equation.id,
      equation.kind,
      equation.scope,
      equation.unit,
      equation.group,
      equation.expression,
      equation.costCategory ?? null,
    ]),
  );

/** Compile errors that belong to no single equation (a missing system output, for instance). */
export const policyWideErrors = (errors: readonly PolicyError[]): PolicyError[] => errors.filter((error) => error.equationId === undefined);

export const errorsByEquation = (errors: readonly PolicyError[]): Map<string, PolicyError[]> => {
  const byEquation = new Map<string, PolicyError[]>();

  for (const error of errors) {
    if (error.equationId === undefined) {
      continue;
    }

    byEquation.set(error.equationId, [...(byEquation.get(error.equationId) ?? []), error]);
  }

  return byEquation;
};

/** The contract's equation shape and the engine's are the same data; this states that in one place. */
export const toEngineEquations = (equations: readonly EquationDefinition[]): EquationDef[] => equations as EquationDef[];
